import { useCallback } from 'react';
import {
  type BrandWithSupport,
  useDeleteBrandWithRelatedData,
} from 'src/graphql/entities/brand';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';

interface StartDeleteParams {
  segment: BrandWithSupport;
  sitesCount: number;
}

const buildBlockedBody = (segmentName: string, sitesCount: number): string => {
  const siteWord = sitesCount === 1 ? 'site' : 'sites';
  if (!sitesCount) {
    return `${segmentName} is in use by one or more sites. Reassign each site to another segment or delete the sites first, then try again.`;
  }
  return `This segment is in use by ${sitesCount} ${siteWord}. Reassign each site to another segment or delete the sites first, then try again.`;
};

export const useClientSegmentDeleteFlow = () => {
  const { confirm } = useConfirmationModal();
  const { showSuccessAlert } = useNotifications();
  const { deleteBrand } = useDeleteBrandWithRelatedData();

  const start = useCallback(
    async ({ segment, sitesCount }: StartDeleteParams) => {
      const segmentName = segment.name ?? 'this segment';

      const proceed = await confirm({
        variant: 'delete',
        title: `Delete ${segmentName}?`,
        body: `This will permanently remove ${segmentName}. Conversations, billing references, and assignments stay intact but lose their brand. This can't be undone.`,
      });
      if (!proceed) return;

      if (segment.partnerId === null || segment.partnerId === undefined) {
        await confirm({
          variant: 'plain',
          title: `Can't delete ${segmentName}`,
          body: 'Cannot delete segment: partner is unknown.',
        });
        return;
      }

      const result = await deleteBrand({
        brandId: segment.brandId,
        partnerId: segment.partnerId,
      });

      if (result.success) {
        showSuccessAlert('Client segment deleted.');
        return;
      }

      await confirm({
        variant: 'plain',
        title: `Can't delete ${segmentName}`,
        body: buildBlockedBody(segmentName, sitesCount),
      });
    },
    [confirm, deleteBrand, showSuccessAlert],
  );

  return { start };
};
